import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

/**
 * Hybrid second-level navigation (Approach G — Master/Detail).
 *
 * Desktop (>=md): vertical left sub-nav sidebar (icon + label + optional count),
 * with the selected sub-module's content rendered to the right.
 * Mobile (<md): the sidebar collapses into a single dropdown (Shadcn Select).
 *
 * Visually distinct from the primary horizontal Tabs row so the UI never feels
 * like "tabs inside tabs". Reusable across all Communication sub-tabs (and future
 * divisions) — only the `items` array changes.
 *
 * Props:
 *  - items: [{ key, label, icon?, count?, content }]
 *  - value: active key (controlled)
 *  - onValueChange: (key) => void
 *  - testIdPrefix
 */
export const SubModuleLayout = ({ items, value, onValueChange, testIdPrefix = 'submodule' }) => {
  const current = items.find((i) => i.key === value) || items[0];

  return (
    <div className="flex flex-col md:flex-row md:gap-6 gap-3" data-testid={`${testIdPrefix}-layout`}>
      {/* Mobile dropdown */}
      <div className="md:hidden">
        <Select value={current.key} onValueChange={onValueChange}>
          <SelectTrigger className="w-full" data-testid={`${testIdPrefix}-mobile-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item.key} value={item.key} data-testid={`${testIdPrefix}-mobile-option-${item.key}`}>
                {item.label}{typeof item.count === 'number' ? ` (${item.count})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop vertical sub-nav */}
      <nav className="hidden md:block w-56 shrink-0" data-testid={`${testIdPrefix}-sidebar`}>
        <div className="space-y-1 sticky top-4">
          {items.map((item) => {
            const Icon = item.icon;
            const active = item.key === current.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => onValueChange(item.key)}
                data-testid={`${testIdPrefix}-nav-${item.key}`}
                className={`w-full flex items-center gap-2.5 pl-3 pr-2 py-2 rounded-md text-sm text-left transition-colors border-l-2 ${
                  active
                    ? 'bg-orange-50 text-orange-700 font-medium border-orange-500'
                    : 'text-slate-600 hover:bg-slate-50 border-transparent'
                }`}
              >
                {Icon && <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-orange-500' : 'text-slate-400'}`} />}
                <span className="flex-1 truncate">{item.label}</span>
                {typeof item.count === 'number' && (
                  <span
                    className={`text-[11px] px-1.5 py-0.5 rounded-full shrink-0 ${
                      active ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {item.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Content */}
      <div className="flex-1 min-w-0" data-testid={`${testIdPrefix}-content`}>
        {current.content}
      </div>
    </div>
  );
};
